import { Component, inject, OnInit } from "@angular/core";
import { NzModalRef, NZ_MODAL_DATA } from "ng-zorro-antd/modal";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzIconModule } from "ng-zorro-antd/icon";
import { HttpClient } from "@angular/common/http";
import { CommonModule } from "@angular/common";
import { CoreTestResponse } from "@handshake/core-types";

interface CoreTestModalData {
  apiUrl: string;
}

@Component({
  selector: "app-core-test-modal",
  standalone: true,
  imports: [NzButtonModule, NzIconModule, CommonModule],
  template: `
    <div class="p-6">
      <div class="flex items-center justify-between mb-6 pb-4 border-b">
        <h2 class="text-xl font-semibold">Core Libraries Test</h2>
        <button nz-button nzType="text" nzShape="circle" (click)="onClose()">
          <i nz-icon nzType="close" nzTheme="outline"></i>
        </button>
      </div>
      
      <div class="space-y-4">
        <div class="text-center">
          <h2 class="text-2xl font-bold text-purple-600 mb-2">Made by Handshake Only</h2>
          <p class="text-gray-600">This modal demonstrates the use of core shared libraries via API</p>
        </div>

        @if (loading) {
          <div class="text-center py-8">
            <i nz-icon nzType="loading" nzTheme="outline" class="text-2xl"></i>
            <p class="mt-2 text-gray-600">Loading test results...</p>
          </div>
        }

        @if (error) {
          <div class="bg-red-50 border border-red-200 rounded-lg p-4">
            <p class="text-red-800">Error: {{ error }}</p>
          </div>
        }

        @if (testData && !loading) {
          <div class="bg-gray-50 rounded-lg p-4 space-y-3">
            <div class="font-semibold text-lg mb-3">Test Results from API:</div>
            
            <!-- Core-API Test -->
            <div class="border-b pb-3">
              <div class="font-medium text-blue-600 mb-2">✓ Core-API (Helper):</div>
              <div class="text-sm text-gray-700">
                <div>Generated Code: <strong>{{ testData.tests.coreApi.generateCode }}</strong></div>
                <div>Snake Case: <strong>{{ testData.tests.coreApi.toSnakeCase }}</strong></div>
              </div>
            </div>

            <!-- Core-Types Test -->
            <div class="border-b pb-3">
              <div class="font-medium text-green-600 mb-2">✓ Core-Types (BaseEntity):</div>
              <div class="text-sm text-gray-700">
                <div>ID: <strong>{{ testData.tests.coreTypes.baseEntity.id }}</strong></div>
                <div>Created: <strong>{{ formatDate(testData.tests.coreTypes.baseEntity.createdAt) }}</strong></div>
                <div>Updated: <strong>{{ formatDate(testData.tests.coreTypes.baseEntity.updatedAt) }}</strong></div>
              </div>
            </div>

            <!-- Core-FE Test -->
            <div>
              <div class="font-medium text-purple-600 mb-2">✓ Core-FE (Modal Service):</div>
              <div class="text-sm text-gray-700">
                Modal opened successfully using NzModalService
              </div>
            </div>
          </div>

          <div class="text-center text-xs text-gray-500 mt-4">
            All core libraries are working correctly! 🎉
          </div>
        }
      </div>
    </div>
  `,
})
export class CoreTestModalComponent implements OnInit {
  modalRef = inject(NzModalRef);
  private http = inject(HttpClient);
  private modalData = inject<CoreTestModalData>(NZ_MODAL_DATA as any);

  loading = false;
  error: string | null = null;
  testData: CoreTestResponse | null = null;

  ngOnInit() {
    this.loadTestData();
  }

  loadTestData() {
    if (!this.modalData?.apiUrl) {
      this.error = "API URL not configured";
      return;
    }

    this.loading = true;
    this.error = null;

    this.http.get<CoreTestResponse>(`${this.modalData.apiUrl}/api/core-test`).subscribe({
      next: (data) => {
        this.testData = data;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || err.message || "Failed to load test data";
        this.loading = false;
      },
    });
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString();
  }

  onClose() {
    this.modalRef.close();
  }
}
